/**
 * Shared "strichliste 365" brand primitives — the self-measuring wordmark
 * line + the M365 cloud gradient. Used by the sidebar (AppShell) and the
 * login screen so the logo stays pixel-identical in both places.
 */

import { useId, useLayoutEffect, useRef, useState } from "react";

export const BRAND_FONT = "'Palapa Neue Linear', system-ui, sans-serif";

// "365" wears the signature M365 cloud gradient (brand blue → sky), tuned per
// mode so it stays luminous on the dark canvas. The "strichliste" word should
// read an explicit theme token (not `currentColor`) at the call site so it's
// correct inside non-themed containers (e.g. the mobile <Drawer>).
export const BRAND_365_GRADIENT = {
  light: { from: "#0078D4", to: "#34C3F0" },
  dark: { from: "#2899FF", to: "#7AD1FF" },
} as const;

// One wordmark line. Rendered at a fixed font-size, then the SVG viewBox is
// tightened to the glyph bounding box (getBBox) and the SVG set to
// width:100% — so the line scales UNIFORMLY (no stretch) to fill its
// container width. Two stacked lines in the same-width container come out the
// SAME width. Re-measures once the webfont loads.
export function FitLine({
  text,
  weight,
  opacity,
  fill = "currentColor",
  gradient,
}: {
  text: string;
  weight: number;
  opacity?: number;
  fill?: string;
  /** Diagonal two-stop gradient fill (overrides `fill`). */
  gradient?: { from: string; to: string };
}) {
  const ref = useRef<SVGTextElement | null>(null);
  const [vb, setVb] = useState<string | null>(null);
  // Unique, SVG-id-safe gradient id (React useId yields ":r0:" which is illegal in an id).
  const gradId = `brand-grad-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const paint = gradient ? `url(#${gradId})` : fill;

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = () => {
      const t = ref.current;
      if (!t) return;
      const b = t.getBBox();
      if (b.width > 0 && b.height > 0 && !cancelled) {
        setVb(`${b.x} ${b.y} ${b.width} ${b.height}`);
      }
    };
    measure();
    if (document.fonts?.ready) {
      void document.fonts.ready.then(() => {
        if (!cancelled) measure();
      });
    }
    return () => {
      cancelled = true;
    };
  }, [text, weight]);

  return (
    <svg
      width="100%"
      viewBox={vb ?? "0 0 600 110"}
      preserveAspectRatio="xMinYMid meet"
      role="img"
      aria-label={text}
      style={{ display: "block", visibility: vb ? "visible" : "hidden" }}
    >
      {gradient && (
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradient.from} />
            <stop offset="100%" stopColor={gradient.to} />
          </linearGradient>
        </defs>
      )}
      <text
        ref={ref}
        x="0"
        y="0"
        dominantBaseline="text-before-edge"
        fontFamily={BRAND_FONT}
        fontWeight={weight}
        fontSize={100}
        fill={paint}
        opacity={opacity}
      >
        {text}
      </text>
    </svg>
  );
}
